import React from 'react';
import { Button, Avatar, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, User } from '@nextui-org/react';
import Lottie from 'lottie-react';
import { useTheme } from 'next-themes';
import UserAnimation from '../lottie/User.json';
import UserIconBlackAnimation from '../lottie/UserIcon-black.json';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const AuthButton: React.FC = () => {
    const { user, logout, setShowAuthModal, loading } = useAuth();
    const navigate = useNavigate();
    const { resolvedTheme } = useTheme();
    const userIconAnimation = resolvedTheme === 'dark' ? UserAnimation : UserIconBlackAnimation;

    interface LottieUserIconProps {
        size?: number;
        height?: number;
        width?: number;
        animationData?: any;
    }

    const LottieUserIcon: React.FC<LottieUserIconProps> = ({ size, height, width, animationData }) => {
        const iconSize = size || height || width || 18;
        const animation = animationData || UserAnimation;

        return (
            <Lottie
                animationData={animation}
                loop={true}
                autoplay={!loading}
                style={{ width: iconSize, height: iconSize }}
            />
        );
    };


    if (loading) {
        return (
            <Button
                size='sm'
                variant='flat'
                isLoading
                className='min-w-[80px]'
            >
                Loading
            </Button>
        );
    }

    if (user) {
        return (
            <Dropdown placement='bottom-end'>
                <DropdownTrigger>
                    <Avatar
                        as='button'
                        className='transition-transform hover:scale-110 cursor-pointer bg-transparent'
                        color='primary'
                        name={user.displayName || user.email || "User"}
                        size='sm'
                        src={undefined}
                        showFallback
                        isBordered
                        fallback={<LottieUserIcon size={24} animationData={userIconAnimation} />}
                    />
                </DropdownTrigger>
                <DropdownMenu
                    aria-label='Profile Actions'
                    variant='flat'
                    onAction={(key) => {
                        if (key === "my-notes") {
                            navigate("/my-notes");
                        }
                    }}
                >
                    <DropdownItem
                        key='profile'
                        className='h-14 gap-2 cursor-default'
                        textValue='Profile'
                        isReadOnly
                    >
                        <User
                            name={user.displayName || user.email?.split('@')[0] || "User"}
                            description={user.email}
                            avatarProps={{
                                src: user.photoURL || undefined,
                                size: "sm",
                                showFallback: true,
                                name: user.displayName || user.email || "User",
                                className: "bg-transparent",
                                fallback: <LottieUserIcon size={24} animationData={userIconAnimation} />
                            }}
                        />
                    </DropdownItem>
                    <DropdownItem
                        key='logout'
                        color='danger'
                        onClick={logout}
                        startContent={
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' />
                            </svg>
                        }
                    >
                        Log Out
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        );
    }

    return (
        <div className='hidden lg:block'>
            <Button
                color='primary'
                onClick={() => setShowAuthModal(true)}
                endContent={<LottieUserIcon />}
            >
                Login
            </Button>
        </div>
    );
};

export default AuthButton; 